import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import events from './locales/en/events.json';
import auth from './locales/en/auth.json';
import common from './locales/en/common.json';
import customers from './locales/en/customers.json';
import markets from './locales/en/markets.json';
import mech from './locales/en/mech.json';
import mechUsers from './locales/en/mechUsers.json';
import mechAssets from './locales/en/mechAssets.json';
import mechInventory from './locales/en/mechInventory.json';
import mechCatalog from './locales/en/mechCatalog.json';
import mechQuality from './locales/en/mechQuality.json';
import mechOperations from './locales/en/mechOperations.json';
import fieldops from './locales/en/fieldops.json';
import home from './locales/en/home.json';
import operations from './locales/en/operations.json';
import maintenance from './locales/en/maintenance.json';
import platform from './locales/en/platform.json';
import plans from './locales/en/plans.json';
import rides from './locales/en/rides.json';
import shell from './locales/en/shell.json';

/**
 * Translations. English is the source and ships in the main bundle; the other languages load on
 * demand (one chunk per language and namespace). See src/ui/README.md, "Translations".
 */
export const resources = {
  events,
  common,
  shell,
  auth,
  home,
  rides,
  customers,
  markets,
  mech,
  mechUsers,
  mechAssets,
  mechInventory,
  mechCatalog,
  mechQuality,
  mechOperations,
  fieldops,
  operations,
  maintenance,
  platform,
  plans,
} as const;
export type Namespace = keyof typeof resources;
export const NAMESPACES = Object.keys(resources) as Namespace[];

/** Supported languages, named in their own language (for the picker). */
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'zh-CN', name: '简体中文' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
] as const;
export type Language = (typeof LANGUAGES)[number]['code'];

/** A key of the sidebar/route titles (shell:nav.*): what FeatureRoute and FeatureSection titles hold. */
export type NavKey = keyof typeof shell.nav;

const STORAGE_KEY = 'manhattan.lang';

/** Lazy loaders for every non-English bundle, by path ("./locales/fr/rides.json"). */
const bundles = import.meta.glob<{ default: Record<string, unknown> }>(['./locales/*/*.json', '!./locales/en/*.json']);

function isLanguage(code: string | null | undefined): code is Language {
  return LANGUAGES.some((l) => l.code === code);
}

/** The remembered choice, else the browser's language when supported (zh, zh-Hans… → zh-CN), else English. */
export function preferredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  for (const tag of typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language]) {
    const lower = tag.toLowerCase();
    if (lower.startsWith('zh')) return lower.includes('tw') || lower.includes('hk') || lower.includes('hant') ? 'en' : 'zh-CN';
    const base = lower.split('-')[0];
    if (base === 'fr' || base === 'es' || base === 'en') return base;
  }
  return 'en';
}

async function load(lng: Language) {
  if (lng === 'en' || i18n.hasResourceBundle(lng, 'common')) return;
  await Promise.all(
    NAMESPACES.map(async (ns) => {
      const loader = bundles[`./locales/${lng}/${ns}.json`];
      if (loader) i18n.addResourceBundle(lng, ns, (await loader()).default, true, true);
    }),
  );
}

function applyDocumentLanguage(lng: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}

i18n.use(initReactI18next).init({
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: LANGUAGES.map((l) => l.code),
  ns: NAMESPACES,
  defaultNS: 'common',
  resources: { en: resources },
  partialBundledLanguages: true,
  interpolation: { escapeValue: false },
  returnNull: false,
  initAsync: false,
  showSupportNotice: false,
});
i18n.on('languageChanged', applyDocumentLanguage);
applyDocumentLanguage('en');

/** Switches the UI language (loading it first) and remembers the choice in this browser. */
export async function setLanguage(lng: Language): Promise<void> {
  await load(lng);
  await i18n.changeLanguage(lng);
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* the choice lasts for this page only */
  }
}

/** Starts in the preferred language; resolve before the first render to avoid an English flash. */
export async function initLanguage(): Promise<void> {
  const lng = preferredLanguage();
  if (lng === 'en') return;
  await load(lng);
  await i18n.changeLanguage(lng);
}

/** The BCP 47 locale for Intl formatting in the active language. */
export function activeLocale(): string {
  const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  return lng === 'en' ? 'en-US' : lng;
}

export { i18n };
